import { type FC, useEffect } from 'react';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { AddLoyaltyIdentifierDocument } from '@packages/gql/generated/shopping/AddLoyaltyIdentifierDocument';
import { LoyaltyFragmentFragmentDoc } from '@packages/gql/generated/shopping/LoyaltyFragmentFragmentDoc';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useEventTracking } from '../Tracking/useEventTracking';

export type PaybackBoxProps = {
  loyaltyProgramData: FragmentType<typeof LoyaltyFragmentFragmentDoc>;
};

export const messages = defineMessages({
  headline: {
    id: 'PaybackBox.Headline',
    defaultMessage: 'PAYBACK PUNKTE',
  },
  defaultDescription: {
    id: 'PaybackBox.Default.Description',
    defaultMessage:
      'Du kannst bei deinem Einkauf {points} sammeln. Trage dazu deine PAYBACK Kundennummer in Schritt 5 ein.',
  },
  loginLink: {
    id: 'PaybackBox.Login.Link',
    defaultMessage: 'Noch kein PAYBACK? Jetzt anmelden!',
  },
  cardPresent: {
    id: 'PaybackBox.CardPresent',
    defaultMessage: 'Deine PAYBACK Kundennummer ist hinterlegt.',
  },
  cardNumberWarningMsg: {
    id: 'PaybaykBox.cardNumber.warning',
    defaultMessage: 'Deine Kundennummer konnte nicht von PAYBACK übernommen werden. Bitte prüfen.',
  },
});

type ScridItem = {
  createdAt: number; // unix timestamp: when the item has been created
  expiresAt: number;
  value: string; // the actual scrid
};

const readScrid = (): ScridItem | null => {
  const item = localStorage.getItem('scrid');

  try {
    const scridItem = item ? (JSON.parse(item) as ScridItem) : null;
    if (scridItem && scridItem.expiresAt < Date.now()) {
      localStorage.removeItem('scrid');
      return null;
    }

    return scridItem;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return null;
  }
};

export const PaybackBox: FC<PaybackBoxProps> = ({ loyaltyProgramData }: PaybackBoxProps) => {
  const { formatMessage, locale } = useIntl();
  const {
    order: { payback },
  } = useConfig();
  const { basePoints, connected } = getFragmentData(LoyaltyFragmentFragmentDoc, loyaltyProgramData);
  const dispatch = useEventTracking();
  const [state, addLoyaltyIdentifier] = useAuthMutation(AddLoyaltyIdentifierDocument);
  const mutationResult = getFragmentData(
    BasketFragmentFragmentDoc,
    state.data?.addLoyaltyIdentifierToBasket,
  );

  useEffect(() => {
    const scrid = readScrid();
    if (scrid) {
      const addLoyaltyIdentifierFromScrid = async () => {
        // IMPORTANT: remove scrid before calling the mutation otherwise this is an endless loop
        // because the mutation triggers a rerendering of this component
        localStorage.removeItem('scrid');
        const result = await addLoyaltyIdentifier({
          locale,
          identifier: scrid.value,
          identifierType: 'Scrid',
        });
        const resultData = getFragmentData(
          BasketFragmentFragmentDoc,
          result.data?.addLoyaltyIdentifierToBasket,
        );
        if (resultData && resultData.success) {
          dispatch('scrid', 'read');
        }
      };
      addLoyaltyIdentifierFromScrid();
    }
    // adding addLoyaltyIdentifier to deps array will cause a non-terminating test
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, payback.enabled, dispatch]);

  if (!payback?.enabled || basePoints === null) {
    return null;
  }

  return (
    <div className="bg-color-payback-light border-color-grey m-1.5 flex rounded-sm border p-2 md:m-0">
      {/* logo  */}
      <div className="pr-1.5">
        <div className="icon-emp-payback-colored size-4" />
      </div>
      <div className="flex w-full flex-col gap-1">
        {/* headline */}
        <div className="flex w-full items-center justify-between font-semibold">
          <p>{formatMessage(messages.headline)}</p>
          {basePoints >= 0 && connected && (
            <p data-testid="Bold-Points">
              {state.fetching ? (
                <Skeleton className="mr-1 inline-block w-6" />
              ) : (
                <FormattedNumber value={basePoints} />
              )}
              °P
            </p>
          )}
        </div>

        {/* Messages */}
        {basePoints >= 0 && !connected && (
          <>
            <p className="mb-4">
              {formatMessage(messages.defaultDescription, {
                points: (
                  <span className="ml-0.5 font-semibold">
                    <FormattedNumber value={basePoints} /> °P
                  </span>
                ),
              })}
            </p>
            <a
              className="text-color-text-darktransparent hover:text-color-text-dark decoration-current"
              href={payback.links?.partner ?? ''}
              target="_blank"
              rel="noreferrer"
            >
              <p>{formatMessage(messages.loginLink)}</p>
            </a>
          </>
        )}
        {connected && (
          <div className="flex items-center gap-1">
            <div>
              <span className="icon-emp-checkmark-inverted-20 text-color-success size-3" />
            </div>
            <div>
              <p className="text-color-success text-size-body-sm">
                {formatMessage(messages.cardPresent)}
              </p>
            </div>
          </div>
        )}
        {mutationResult && !mutationResult.success && (
          <Notification severity="warning">
            {formatMessage(messages.cardNumberWarningMsg)}
          </Notification>
        )}
      </div>
    </div>
  );
};
